import React, { ReactNode, forwardRef } from 'react';
import Styled from './styled';

export type MenuItemProps = {
    /** Unique key for the menu item. */
    key: string;
    /** Icon to be displayed for the menu item. */
    icon: ReactNode;
    /** Label text for the menu item. */
    label: string;
    /** Click handler for the menu item */
    onClick: () => void;
    /** Optional, should menu close after selecting menu item. */
    closeMenu?: boolean;
};

export type MoreMenuProps = {
    /** Optional class name for custom styling. */
    className?: string;
    /** List of menu items to be displayed. */
    menuItems: MenuItemProps[];
    /** Function to set the menu visibility. */
    setMenuVisible: (visible: boolean) => void;
};

/**
 * MoreMenu component that renders a list of menu items.
 * This component is wrapped with forwardRef to allow the parent component to access its ref.
 * 
 * @param {MoreMenuProps} props - The properties to configure the MoreMenu component.
 * @returns {JSX.Element} The rendered MoreMenu component.
 */
export const MoreMenu = forwardRef<HTMLDivElement, MoreMenuProps>(({
    className,
    menuItems,
    setMenuVisible,
}, ref) => {

    const handleMenuItemAction = (item: MenuItemProps) => {
        item.onClick();
        if (item.closeMenu) {
            setMenuVisible(false);
        }
    };

    return (
        <Styled.MoreMenu ref={ref} className={className}>
            {menuItems.map(item => (
                <Styled.MenuItem
                    key={item.key}
                    onClick={() => {
                        handleMenuItemAction(item)
                    }}
                    aria-label={item.label}
                    tabIndex={2}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            handleMenuItemAction(item)
                        }
                    }}
                >
                    <Styled.MenuIcon>{item.icon}</Styled.MenuIcon>
                    {item.label}
                </Styled.MenuItem>
            ))}
        </Styled.MoreMenu>
    );
});
